/**
 * LAP-E EZ EGYÁLTALÁN? — a felismert négyszög ésszerűségi szűrője (#88).
 *
 * A `scanic` a képkocka LEGERŐSEBB négyszögét adja vissza, és ez nem mindig
 * a lap. Viktor telefonos próbája (2026-11-02): egy számlán a TÉTELTÁBLA
 * kerete — erős, egyenes vonal — győzött a halvány lapél fölött, és a kivágás
 * a fejlécet, a partnert és a keltet levágta. „Ne csak a tétel részt
 * szkennelje, hanem az egész oldalt."
 *
 * A ROSSZ VÁGÁS ROSSZABB A VÁGATLANNÁL: a levágott rész végleg elvész, a
 * vágatlan kép viszont minden adatot megőriz (a könyvelő elolvassa, a
 * kinyerés is megpróbálja). Kétes négyszögnél ezért NEM vágunk — a hívó
 * úgy kezeli, mintha nem talált volna lapot.
 *
 * ⚠️ A KÜSZÖBÖK MÉRETLENEK, mint a kamera többi száma. A telefonos próba dönt.
 */

import type { QuadCorners } from './document-scanner';

/**
 * A LEGLAPOSABB ELFOGADOTT ARÁNY (szélesség : magasság).
 *
 * Egy fekvő A4 1,41 : 1, ferde szögből fotózva valamivel több. A próbán
 * kivágott tételtábla ~4,4 : 1 volt. A 2 a kettő között bőven elválaszt.
 * A keskeny, HOSSZÚ blokk (álló, ~0,3 : 1) ettől nem esik ki — arra nincs
 * alsó határ.
 */
export const MAX_PAGE_ASPECT = 2;

/**
 * A LEGKISEBB ELFOGADOTT TERÜLET a képkocka arányában.
 *
 * A célkeret (`.camera-edge`) a kép ~58%-a — az ügyfelet arra kérjük, hogy
 * a lap töltse ki. Egy ennél jóval kisebb négyszög inkább a lapon BELÜLI
 * keret (táblázat, bélyegzőmező, logó), mint maga a lap.
 */
export const MIN_PAGE_AREA_RATIO = 0.15;

const distance = (a: { x: number; y: number }, b: { x: number; y: number }) =>
  Math.hypot(a.x - b.x, a.y - b.y);

/** A négyszög területe (cipőfűző-képlet) — a sarkok körbejárási sorrendjében. */
function area(corners: QuadCorners): number {
  const points = [corners.topLeft, corners.topRight, corners.bottomRight, corners.bottomLeft];
  let sum = 0;
  for (let index = 0; index < points.length; index += 1) {
    const current = points[index];
    const next = points[(index + 1) % points.length];
    sum += current.x * next.y - next.x * current.y;
  }
  return Math.abs(sum) / 2;
}

export function isPlausiblePage(
  corners: QuadCorners,
  frameWidth: number,
  frameHeight: number
): boolean {
  // A SZÉLESSÉG ÉS A MAGASSÁG A SZEMKÖZTI ÉLEK ÁTLAGA — perspektívában a
  // két él nem egyforma, és egyiket kiválasztani önkényes volna.
  const width =
    (distance(corners.topLeft, corners.topRight) +
      distance(corners.bottomLeft, corners.bottomRight)) /
    2;
  const height =
    (distance(corners.topLeft, corners.bottomLeft) +
      distance(corners.topRight, corners.bottomRight)) /
    2;

  if (width === 0 || height === 0) {
    return false;
  }
  if (width / height > MAX_PAGE_ASPECT) {
    return false;
  }
  return area(corners) >= MIN_PAGE_AREA_RATIO * frameWidth * frameHeight;
}
